import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [agreePrivacy, setAgreePrivacy] = useState(false);

  const services = [
    'iGaming & FX Licensing',
    'AML & KYC Outsourcing',
    'Compliance Outsourcing',
    'Tax & Payroll Outsourcing',
    'Accounting Outsourcing',
    'Legal Drafting',
    'Company Formation',
    'Blockchain & Crypto',
    'Residency Programs',
    'Other',
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreePrivacy) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6n8i5qqoe30lj0v8jt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setAgreePrivacy(false);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
      <div className="mb-6">
        <span className="inline-block px-3 py-1 bg-amber-50 border border-amber-200 text-amber-600 text-xs font-semibold uppercase tracking-widest rounded mb-3">
          Free Consultation
        </span>
        <h2 className="text-2xl font-semibold text-slate-900">Get Expert Advice Today</h2>
        <p className="text-slate-500 text-sm mt-1">
          Fill in the form and one of our specialists will be in touch within 24 hours.
        </p>
      </div>

      <form id="contact-page-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="cp-name" className="block text-sm font-medium text-slate-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="cp-name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Your full name"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
            />
          </div>
          <div>
            <label htmlFor="cp-email" className="block text-sm font-medium text-slate-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="cp-email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="your@email.com"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="cp-phone" className="block text-sm font-medium text-slate-700 mb-1">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="cp-phone"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              placeholder="+357 ..."
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
            />
          </div>
          <div>
            <label htmlFor="cp-service" className="block text-sm font-medium text-slate-700 mb-1">
              Service of Interest
            </label>
            <select
              id="cp-service"
              name="service"
              value={formData.service}
              onChange={(e) => setFormData({ ...formData, service: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm bg-white cursor-pointer"
            >
              <option value="">Select a service...</option>
              {services.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="cp-message" className="block text-sm font-medium text-slate-700 mb-1">
            Message
          </label>
          <textarea
            id="cp-message"
            name="message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            maxLength={500}
            rows={4}
            placeholder="Tell us about your needs..."
            className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none text-sm"
          ></textarea>
          <p className="text-xs text-slate-400 mt-1 text-right">{formData.message.length}/500</p>
        </div>

        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="cp-privacy"
            checked={agreePrivacy}
            onChange={(e) => setAgreePrivacy(e.target.checked)}
            required
            className="mt-1 w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500 cursor-pointer"
          />
          <label htmlFor="cp-privacy" className="text-sm text-slate-600 cursor-pointer">
            I agree to the{' '}
            <Link to="/privacy-policy" className="text-amber-500 underline hover:text-amber-600">
              Privacy Policy
            </Link>
            . <span className="text-red-500">*</span>
          </label>
        </div>

        {submitStatus === 'success' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-green-500 text-lg"></i>
            Thank you! We&apos;ll be in touch within 24 hours.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm flex items-center gap-2">
            <i className="ri-error-warning-line text-red-500 text-lg"></i>
            Something went wrong. Please try again or call us directly.
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !agreePrivacy}
          className="w-full px-6 py-4 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i> Sending...
            </span>
          ) : (
            'Book a Free Consultation'
          )}
        </button>
      </form>
    </div>
  );
}
